/**
 * How a roster row's attribute PROVENANCE should read — where the values came
 * from and how old they are.
 *
 * This exists because `reportedAt`/`reportedBy` were kept as siblings of
 * `values` rather than folded in, at this lane's request: **"how stale" is the
 * first question about a `known` attribute.** lexserver protected the fields in
 * their guide so a later tidy-up has to argue with it. Consuming them is the
 * other half of that bargain — a field you had protected and never render is
 * worse than one you never asked for.
 *
 * ⚠⚠ AND DELIBERATELY WITHOUT A STALENESS VERDICT. THE REASONING IS THE POINT.
 *
 * The obvious build is an amber "stale" badge past some threshold. There is no
 * threshold to pick. The reporter fires **once per boot-scan, which is the only
 * scan** (`harn-service/src/server.ts`: "rescan retired: manifests change only
 * via deploy, and every deploy restarts this process") — so there is NO cadence
 * to judge an age against. A `reportedAt` from three days ago is entirely normal
 * on a fleet that has not rolled in three days.
 *
 * An amber badge there would be a false alarm on every healthy runtime that
 * simply has not restarted — which is precisely the defect this surface's jobs
 * panel shipped and had to fix hours earlier (`null` rendered as "never ran",
 * loud after every deploy). The lesson transferred rather than being relearned:
 * **an alarm needs a cadence to be alarming ABOUT.** S7's jobs have one
 * (`intervals`), so `jobStaleness` judges there; this has none, so this does not.
 *
 * ⇒ What is honest instead: the AGE, plainly, plus the MECHANISM that lets an
 * operator interpret it themselves. "Reported 3d ago, at runtime boot" is a
 * fact; "STALE" would be a verdict nothing supports.
 */

import { describeAge } from "./specialist-jobs";

/** What the roster's provenance cell should show. */
export type Provenance =
  /** The attributes column itself is unknown — there is no provenance to show,
   *  and the cell must not imply one. */
  | { kind: "none" }
  | {
      kind: "reported";
      /** Coarse age, e.g. "12m ago". Never accompanied by a verdict. */
      age: string;
      /** The full sentence for a tooltip: exact time, reporter, manifest hash,
       *  and the mechanism note. */
      detail: string;
    };

export interface ProvenanceInput {
  status: string;
  reportedAt?: string;
  reportedBy?: string;
  manifestHash?: string;
}

const MECHANISM =
  "Attributes are packaged in the lexruntime image and reach the platform only by report. " +
  "A runtime reports once at boot — there is no periodic re-report, so an old timestamp means " +
  "the runtime has not restarted, not that anything is wrong.";

export function describeProvenance(
  col: ProvenanceInput | null | undefined,
  now: number,
): Provenance {
  if (!col || col.status !== "known") return { kind: "none" };

  const at = col.reportedAt ? Date.parse(col.reportedAt) : NaN;
  if (!Number.isFinite(at)) {
    // Known values with no usable timestamp. Say so rather than showing a
    // confident age — the same refusal-to-guess the column union itself models.
    return {
      kind: "reported",
      age: "unknown age",
      detail: `This specialist's attributes are present, but no usable report time came with them. ${MECHANISM}`,
    };
  }

  const parts = [`Reported ${new Date(at).toLocaleString()}`];
  // `reportedBy` is legitimately absent — the runtime reporter stamps it only
  // when it knows its own userId. Absent is not a fault, so it is stated
  // neutrally rather than flagged.
  parts.push(col.reportedBy ? `by ${col.reportedBy}` : "by an unidentified runtime");
  if (col.manifestHash) parts.push(`· manifest ${col.manifestHash.slice(0, 12)}`);

  return {
    kind: "reported",
    age: describeAge(now - at),
    detail: `${parts.join(" ")}. ${MECHANISM}`,
  };
}
